import React, { useState } from 'react';
import { X, Mail, Phone, Gift, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const WelcomeModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const handleContinue = () => {
    if (step === 1 && email) {
      setStep(2);
    } else if (step === 2) {
      onClose();
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <div className="relative">
          {/* Header with Icon */}
          <div className="bg-gradient-to-br from-teal-500 to-cyan-600 pt-8 pb-12 px-6 text-center">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Welcome to Our Store!</h2>
            <p className="text-teal-100 mt-2 text-sm">
              Sign up now and get an exclusive discount and credit $5 on your first order!
            </p>
          </div>

          {/* Discount Badge */}
          <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full shadow-lg">
              <Gift className="w-4 h-4" />
              <span className="font-semibold">$6 + 14% additional discount</span>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 pt-10">
            {/* Steps Indicator */}
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-teal-600' : 'text-gray-400'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 1 ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  1
                </span>
                <span className="font-medium">Contact</span>
              </div>
              <div className="w-8 h-px bg-gray-300" />
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-teal-600' : 'text-gray-400'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 2 ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  2
                </span>
                <span className="font-medium">Password</span>
              </div>
            </div>

            {step === 1 ? (
              <div className="space-y-4">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Phone Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-gray-400">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (234) 567-8900"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">For order updates & exclusive SMS deals</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Create Password <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="Enter password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="Confirm password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                  />
                </div>
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              className="w-full mt-6 flex items-center justify-center gap-2 py-3 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>

            {/* Skip & Sign In Links */}
            <div className="text-center mt-4 space-y-2">
              <button
                onClick={handleSkip}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip for now
              </button>
              <p className="text-sm text-gray-500">
                Already have an account?{' '}
                <button className="text-teal-600 font-medium hover:underline">Sign In</button>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
